import { z } from 'zod';

import { ApiResponseError, assertOk, parseJson } from './response';
import { mergeNetworkMenuSnapshot, recordMenuFallbackUsage } from '@/lib/offline/menuCache';
import { emitMenuConflictTelemetry } from '@/lib/offline/telemetry';
import { normalizeImageUrl } from '@/lib/images/normalizeImageUrl';
import type {
  MenuAvailabilityRules,
  MenuAvailabilityState,
  MenuAvailabilityReason,
  MenuAvailabilityReasonCode,
  MenuAvailabilityScope,
  MenuAvailabilityWindow,
  MenuSpiceLevel,
  DaypartRule,
  HolidayRule,
  LimitedRunWindow,
  DayId,
} from '@/types/menuAvailability';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

type RequestOptions<T> = {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  tenantSlug?: string;
  headers?: Record<string, string>;
  schema?: z.ZodType<T>;
};

async function requestJson<T>(
  path: string,
  { method = 'GET', body, signal, tenantSlug, headers = {}, schema }: RequestOptions<T> = {},
): Promise<T> {
  const apiFetch = await resolveApiFetch();
  const targetSchema = schema ?? (z.unknown() as z.ZodType<T>);
  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  };
  if (tenantSlug) {
    requestHeaders['X-Tenant'] = tenantSlug;
  }

  const shouldStringify = body !== undefined && !(body instanceof FormData);
  if (shouldStringify && !requestHeaders['Content-Type']) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(path, {
    method,
    body: shouldStringify ? JSON.stringify(body) : (body as BodyInit | undefined),
    signal,
    headers: requestHeaders,
  });

  if (response.status === 204) {
    await assertOk(response);
    const parsed = targetSchema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiResponseError('Expected JSON response', response.status);
    }
    return parsed.data;
  }

  await assertOk(response);
  return parseJson(response, targetSchema);
}

export interface MenuItemModifier {
  id: string;
  itemId: string | null;
  groupId: string | null;
  code: string | null;
  sku: string | null;
  externalReference: string | null;
  name: string;
  minQuantity: number;
  maxQuantity: number | null;
  priceDeltaCents: number;
  priceOverrideCents: number | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  allergenTags: string[];
  metadata: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  translations?: Record<string, { name?: string }>;
}

export interface MenuModifierGroup {
  id: string;
  itemId: string;
  code: string | null;
  name: string;
  description: string | null;
  kind: 'modifier' | 'variant';
  selectionMode: 'single' | 'multiple';
  minSelections: number;
  maxSelections: number | null;
  stackingBehavior: 'exclusive' | 'stack' | 'repeatable';
  allowCustomPrice: boolean;
  allowQuantity: boolean;
  isRequired: boolean;
  metadata: Record<string, unknown>;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
  translations?: Record<string, { name?: string; description?: string }>;
  modifiers: MenuItemModifier[];
}

export interface MenuItem {
  id: string;
  name: string;
  description?: string | null;
  priceCents: number;
  taxRateBps?: number | null;
  isEightySixed: boolean;
  isAvailable: boolean;
  categoryId: string | null;
  sortOrder: number;
  availability: MenuAvailabilityState;
  availabilityRules: MenuAvailabilityRules;
  soldOutUntil: number | null;
  autoExpireAt: number | null;
  categoryAutoExpireAt: number | null;
  modifiers?: MenuItemModifier[];
  modifierGroups?: MenuModifierGroup[];
  tags?: string[];
  prepMinutes?: number | null;
  imageUrl?: string | null;
  imageAltText?: string | null;
  imageSpiceLevel?: MenuSpiceLevel | null;
  translations?: Record<string, { name?: string; description?: string; imageAltText?: string }>;
}

export interface MenuCategory {
  id: string;
  name: string;
  sortOrder: number;
  position: number;
  isActive?: boolean;
  availabilityRules?: MenuAvailabilityRules;
  autoExpireAt?: number | null;
  availability?: MenuAvailabilityState;
  createdAt?: number | null;
  updatedAt?: number | null;
  translations?: Record<string, { name?: string; description?: string }>;
  items: MenuItem[];
}

export interface FetchMenuResponse {
  categories: MenuCategory[];
  timezone?: string;
  tenantId?: string | null;
  cacheStatus?: 'network' | 'cache';
  cachedAt?: number;
}

export type MenuEventType = 'view' | 'interaction' | 'order' | 'availability';

export interface MenuEventPayload {
  eventType: MenuEventType;
  menuItemId?: string;
  categoryId?: string;
  locationId?: string;
  occurredAt?: number;
  payload?: Record<string, unknown>;
}

export interface MenuMetricsCounts {
  totalItems: number;
  totalCategories: number;
  eightySixed: number;
  modifierSets: number;
}

export interface MenuMetricsViewsByDate {
  date: string;
  viewCount: number;
  orderCount: number;
}

export interface MenuMetricsViews {
  total: number;
  byDate: MenuMetricsViewsByDate[];
}

export interface MenuMetricsConversions {
  totalOrders: number;
  conversionRate: number;
}

export interface MenuMostViewedItem {
  id: string;
  name: string;
  viewCount: number;
  categoryId: string | null;
}

export interface MenuMetricsSummary {
  counts: MenuMetricsCounts;
  views: MenuMetricsViews;
  conversions: MenuMetricsConversions;
  mostViewed: MenuMostViewedItem | null;
}

export interface FetchMenuMetricsResponse {
  windowDays: number;
  summary: MenuMetricsSummary;
}

export interface MenuAnalyticsTopItem {
  id: string;
  name: string;
  categoryId: string | null;
  priceCents: number;
  viewCount: number;
  orderCount: number;
}

export interface FetchMenuTopItemsResponse {
  windowDays: number;
  items: MenuAnalyticsTopItem[];
}

export interface ReorderMenuCategoryInput {
  id: string;
  sortOrder: number;
}

export interface ReorderMenuItemInput {
  id: string;
  categoryId: string;
  sortOrder: number;
}

export interface UpdateMenuCategoryPayload {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  availabilityRules?: MenuAvailabilityRules;
  autoExpireAt?: number | null;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string; description?: string }>>;
}

export interface UpdateMenuItemPayload {
  name?: string;
  description?: string | null;
  priceCents?: number;
  taxRateBps?: number | null;
  isActive?: boolean;
  categoryId?: string;
  sortOrder?: number;
  imageUrl?: string | null;
  availabilityRules?: MenuAvailabilityRules;
  autoExpireAt?: number | null;
  imageAltText?: string | null;
  imageSpiceLevel?: MenuSpiceLevel | null;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string; description?: string; imageAltText?: string }>>;
}

type MenuApiCategory = {
  id: string;
  name: string;
  sort_order: number | null;
  is_active?: boolean | null;
  availability_rules?: unknown;
  auto_expire_at?: number | null;
  availability?: unknown;
  translations?: Record<string, unknown>;
};

type MenuApiItem = {
  id: string;
  category_id: string | null;
  name: string;
  description: string | null;
  price_cents: number;
  is_available: boolean | null;
  tax_rate_bps?: number | null;
  prep_minutes?: number | null;
  image_url?: string | null;
  sort_order?: number | null;
  availability?: unknown;
  availability_rules?: unknown;
  auto_expire_at?: number | null;
  category_auto_expire_at?: number | null;
  sold_out_until?: number | null;
  image_alt_text?: string | null;
  image_spice_level?: string | null;
  translations?: Record<string, unknown>;
};

type MenuApiResponse = {
  categories?: MenuApiCategory[];
  items?: MenuApiItem[];
  modifier_groups?: unknown[];
  tenant_id?: string | null;
  tenantId?: string | null;
  timezone?: string | null;
};

const UnknownRecordSchema = z.record(z.string(), z.unknown());

const MenuApiResponseSchema = z
  .object({
    categories: z.array(UnknownRecordSchema).optional(),
    items: z.array(UnknownRecordSchema).optional(),
    modifier_groups: z.array(UnknownRecordSchema).optional(),
    tenant_id: z.string().nullable().optional(),
    tenantId: z.string().nullable().optional(),
    timezone: z.string().nullable().optional(),
  })
  .passthrough();

const MenuItemEnvelopeSchema = z
  .object({
    item: UnknownRecordSchema.optional(),
    menuItem: UnknownRecordSchema.optional(),
    items: z.array(UnknownRecordSchema).optional(),
  })
  .passthrough();

const MenuCategoryEnvelopeSchema = z
  .object({
    category: UnknownRecordSchema.optional(),
    categories: z.array(UnknownRecordSchema).optional(),
  })
  .passthrough();

const UpdatedResponseSchema = z
  .object({
    updated: z.number(),
  })
  .passthrough();

const MenuMetricsCountsSchema = z
  .object({
    totalItems: z.number(),
    totalCategories: z.number(),
    eightySixed: z.number(),
    modifierSets: z.number(),
  })
  .passthrough();

const MenuMetricsViewsByDateSchema = z
  .object({
    date: z.string(),
    viewCount: z.number(),
    orderCount: z.number(),
  })
  .passthrough();

const MenuMetricsViewsSchema = z
  .object({
    total: z.number(),
    byDate: z.array(MenuMetricsViewsByDateSchema),
  })
  .passthrough();

const MenuMetricsConversionsSchema = z
  .object({
    totalOrders: z.number(),
    conversionRate: z.number(),
  })
  .passthrough();

const MenuMostViewedItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    viewCount: z.number(),
    categoryId: z.string().nullable(),
  })
  .passthrough();

const MenuMetricsResponseSchema = z
  .object({
    windowDays: z.number(),
    summary: z
      .object({
        counts: MenuMetricsCountsSchema,
        views: MenuMetricsViewsSchema,
        conversions: MenuMetricsConversionsSchema,
        mostViewed: MenuMostViewedItemSchema.nullable(),
      })
      .passthrough(),
  })
  .passthrough();

const MenuTopItemsResponseSchema = z
  .object({
    windowDays: z.number(),
    items: z.array(
      z
        .object({
          id: z.string(),
          name: z.string(),
          categoryId: z.string().nullable(),
          priceCents: z.number(),
          viewCount: z.number(),
          orderCount: z.number(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

const ModifierGroupsResponseSchema = z
  .object({
    groups: z.array(UnknownRecordSchema).optional(),
  })
  .passthrough();

const ModifierGroupEnvelopeSchema = z
  .object({
    group: UnknownRecordSchema,
  })
  .passthrough();

const ModifierListResponseSchema = z
  .object({
    modifiers: z.array(UnknownRecordSchema).optional(),
  })
  .passthrough();

type MenuItemEnvelope = z.infer<typeof MenuItemEnvelopeSchema>;
type MenuCategoryEnvelope = z.infer<typeof MenuCategoryEnvelopeSchema>;
type ModifierGroupEnvelope = z.infer<typeof ModifierGroupEnvelopeSchema>;
type ModifierGroupsResponse = z.infer<typeof ModifierGroupsResponseSchema>;

const UNCATEGORISED_CATEGORY_ID = 'uncategorized';

const SPICE_LEVELS: MenuSpiceLevel[] = ['none', 'mild', 'medium', 'hot', 'extra_hot'];

const SUPPORTED_MENU_LANGUAGES = ['en', 'es'] as const;
type SupportedMenuLanguage = (typeof SUPPORTED_MENU_LANGUAGES)[number];
const SUPPORTED_MENU_LANGUAGE_SET = new Set<string>(SUPPORTED_MENU_LANGUAGES);

const normalizeTranslationLanguage = (value: string): SupportedMenuLanguage | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized.length === 0) {
    return null;
  }
  const [base] = normalized.split(/[-_]/);
  if (base && SUPPORTED_MENU_LANGUAGE_SET.has(base)) {
    return base as SupportedMenuLanguage;
  }
  return null;
};

const prepareTranslationsPayload = <K extends string>(
  input: Partial<Record<string, Partial<Record<K, string>>>> | null | undefined,
  keys: readonly K[],
): Record<string, Partial<Record<K, string>>> => {
  if (!input || typeof input !== 'object') {
    return {};
  }
  const result: Record<string, Partial<Record<K, string>>> = {};
  for (const [language, rawEntry] of Object.entries(input)) {
    if (!rawEntry || typeof rawEntry !== 'object') {
      continue;
    }
    const normalizedLanguage = normalizeTranslationLanguage(language);
    if (!normalizedLanguage) {
      continue;
    }
    const entry: Partial<Record<K, string>> = {};
    for (const key of keys) {
      const rawValue = (rawEntry as Record<string, unknown>)[key];
      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (trimmed.length > 0) {
          entry[key] = trimmed;
        }
      }
    }
    if (Object.keys(entry).length > 0) {
      result[normalizedLanguage] = entry;
    }
  }
  return result;
};

const normaliseTranslationRecord = <K extends string>(
  input: unknown,
  keys: readonly K[],
): Record<string, Partial<Record<K, string>>> => {
  if (!input || typeof input !== 'object') {
    return {};
  }
  const result: Record<string, Partial<Record<K, string>>> = {};
  for (const [language, rawEntry] of Object.entries(input as Record<string, unknown>)) {
    if (!rawEntry || typeof rawEntry !== 'object') {
      continue;
    }
    const normalizedLanguage = normalizeTranslationLanguage(language);
    if (!normalizedLanguage) {
      continue;
    }
    const entry: Partial<Record<K, string>> = {};
    for (const key of keys) {
      const rawValue = (rawEntry as Record<string, unknown>)[key];
      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (trimmed.length > 0) {
          entry[key] = trimmed;
        }
      }
    }
    if (Object.keys(entry).length > 0) {
      result[normalizedLanguage] = entry;
    }
  }
  return result;
};

const normaliseCategoryTranslations = (
  input: unknown,
): Record<string, { name?: string; description?: string }> =>
  normaliseTranslationRecord<'name' | 'description'>(input, ['name', 'description']) as Record<string, { name?: string; description?: string }>;

const normaliseItemTranslations = (
  input: unknown,
): Record<string, { name?: string; description?: string; imageAltText?: string }> =>
  normaliseTranslationRecord<'name' | 'description' | 'imageAltText'>(input, ['name', 'description', 'imageAltText']) as Record<string, { name?: string; description?: string; imageAltText?: string }>;

const normaliseModifierGroupTranslations = (
  input: unknown,
): Record<string, { name?: string; description?: string }> =>
  normaliseTranslationRecord<'name' | 'description'>(input, ['name', 'description']) as Record<string, { name?: string; description?: string }>;

const normaliseModifierTranslations = (
  input: unknown,
): Record<string, { name?: string }> =>
  normaliseTranslationRecord<'name'>(input, ['name']) as Record<string, { name?: string }>;

function normaliseMenuCategory(raw: MenuApiCategory | Record<string, unknown>): MenuCategory {
  const source = raw as Record<string, unknown>;
  const id = typeof source.id === 'string' && source.id.trim().length ? source.id : '';
  if (!id) {
    throw new Error('Menu category payload missing id');
  }

  const nameSource = source.name;
  const name =
    typeof nameSource === 'string' && nameSource.trim().length ? nameSource : 'Untitled Category';

  const orderSource = source.sort_order ?? source.sortOrder ?? source.position ?? 0;
  const order = coerceNumber(orderSource, 0);

  const isActive = typeof source.is_active === 'boolean' ? source.is_active : typeof source.isActive === 'boolean' ? source.isActive : true;
  const availabilityRules = normaliseAvailabilityRules(source.availability_rules ?? source.availabilityRules);
  const autoExpireAt = coerceNullableNumber(source.auto_expire_at ?? source.autoExpireAt);
  const availability = normaliseAvailabilityState(source.availability, isActive);
  const createdAt = coerceNullableNumber(source.created_at ?? source.createdAt);
  const updatedAt = coerceNullableNumber(source.updated_at ?? source.updatedAt);

  return {
    id,
    name,
    sortOrder: order,
    position: order,
    isActive,
    availabilityRules,
    autoExpireAt,
    availability,
    createdAt,
    updatedAt,
    translations: normaliseCategoryTranslations(source.translations),
    items: [],
  };
}

function normaliseMenuResponse(api: MenuApiResponse | null | undefined): FetchMenuResponse {
  const timezoneValue = api?.timezone ?? (api as { timezone?: unknown } | null | undefined)?.timezone;
  const timezone =
    typeof timezoneValue === 'string' && timezoneValue.trim().length > 0 ? timezoneValue : undefined;
  const tenantIdSource = api?.tenantId ?? api?.tenant_id;
  const tenantId =
    typeof tenantIdSource === 'string' && tenantIdSource.trim().length > 0 ? tenantIdSource : null;
  const rawCategories = Array.isArray(api?.categories) ? api!.categories : [];
  const categories = rawCategories.map(normaliseMenuCategory);
  const bucket = new Map<string, MenuItem[]>();
  const itemMap = new Map<string, MenuItem>();
  const groupsByItem = new Map<string, MenuModifierGroup[]>();

  if (Array.isArray(api?.modifier_groups)) {
    for (const rawGroup of api!.modifier_groups!) {
      if (!rawGroup || typeof rawGroup !== 'object') {
        continue;
      }
      try {
        const group = mapGroupResponse(rawGroup as Record<string, unknown>);
        const groups = groupsByItem.get(group.itemId) ?? [];
        groups.push(group);
        groupsByItem.set(group.itemId, groups);
      } catch (error) {
        console.warn('[menu] Skipped invalid modifier group payload', error);
      }
    }
  }

  const appendItem = (rawItem: unknown, fallbackCategoryId?: string): void => {
    try {
      const normalised = normaliseMenuItemPayload(rawItem);
      const categoryId = normalised.categoryId ?? fallbackCategoryId ?? null;
      const finalItem = { ...normalised, categoryId };

      const groups = groupsByItem.get(finalItem.id) ?? [];
      if (groups.length > 0) {
        const sortedGroups = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
        finalItem.modifierGroups = sortedGroups.map((group) => ({
          ...group,
          modifiers: [...group.modifiers].sort((a, b) => a.sortOrder - b.sortOrder),
        }));
        finalItem.modifiers = finalItem.modifierGroups.flatMap((group) => group.modifiers);
      }

      itemMap.set(finalItem.id, finalItem);
    } catch (error) {
      console.warn('[menu] Skipped invalid menu item payload', error);
    }
  };

  for (const category of rawCategories) {
    const fallbackCategoryId =
      typeof (category as { id?: unknown }).id === 'string' ? (category as { id?: string }).id! : undefined;
    const nestedItems = Array.isArray((category as { items?: unknown }).items)
      ? ((category as { items: unknown[] }).items ?? [])
      : [];
    nestedItems.forEach((rawItem) => appendItem(rawItem, fallbackCategoryId));
  }

  (api?.items ?? []).forEach((rawItem) => appendItem(rawItem));

  for (const item of itemMap.values()) {
    const key = item.categoryId ?? UNCATEGORISED_CATEGORY_ID;
    const list = bucket.get(key) ?? [];
    list.push(item);
    bucket.set(key, list);
  }

  categories.sort((a, b) => a.sortOrder - b.sortOrder);

  const result: MenuCategory[] = categories.map((category) => {
    const itemsForCategory = [...(bucket.get(category.id) ?? [])];
    itemsForCategory.sort((a, b) => {
      if (a.sortOrder === b.sortOrder) {
        return a.name.localeCompare(b.name);
      }
      return a.sortOrder - b.sortOrder;
    });
    return {
      ...category,
      items: itemsForCategory,
    };
  });

  const uncategorisedItems = [...(bucket.get(UNCATEGORISED_CATEGORY_ID) ?? [])];
  if (uncategorisedItems.length) {
    uncategorisedItems.sort((a, b) => {
      if (a.sortOrder === b.sortOrder) {
        return a.name.localeCompare(b.name);
      }
      return a.sortOrder - b.sortOrder;
    });
    result.push({
      id: UNCATEGORISED_CATEGORY_ID,
      name: 'Other Items',
      sortOrder: Number.MAX_SAFE_INTEGER,
      position: Number.MAX_SAFE_INTEGER,
      items: uncategorisedItems,
    });
  }

  return { categories: result, timezone, tenantId };
}

function coerceNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function coerceNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = coerceNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

const defaultAvailabilityState = (isAvailable = false): MenuAvailabilityState => ({
  status: isAvailable ? 'available' : 'inactive',
  isAvailable,
  reasons: [],
  nextAvailableAt: null,
  currentWindow: null,
  upcomingWindows: [],
  soldOutUntil: null,
  autoExpireAt: null,
  categoryAutoExpireAt: null,
});

const normaliseAvailabilityWindow = (input: unknown): MenuAvailabilityWindow => {
  if (!input || typeof input !== 'object') {
    return { start: null, end: null };
  }
  const source = input as { start?: unknown; end?: unknown };
  return {
    start: typeof source.start === 'number' && Number.isFinite(source.start) ? source.start : null,
    end: typeof source.end === 'number' && Number.isFinite(source.end) ? source.end : null,
  };
};

const normaliseAvailabilityReasons = (input: unknown): MenuAvailabilityReason[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return input
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const code = typeof record.code === 'string' ? (record.code as MenuAvailabilityReasonCode) : null;
      const scope = typeof record.scope === 'string' ? (record.scope as MenuAvailabilityScope) : 'item';
      const message = typeof record.message === 'string' ? record.message : undefined;
      return code ? { code, scope, message } : null;
    })
    .filter((reason): reason is MenuAvailabilityReason => reason !== null);
};

const normaliseAvailabilityState = (input: unknown, fallbackAvailable = false): MenuAvailabilityState => {
  if (!input || typeof input !== 'object') {
    return defaultAvailabilityState(fallbackAvailable);
  }
  const record = input as Record<string, unknown>;
  const status = typeof record.status === 'string' ? (record.status as MenuAvailabilityState['status']) : fallbackAvailable ? 'available' : 'inactive';
  const isAvailable = typeof record.isAvailable === 'boolean' ? record.isAvailable : fallbackAvailable;
  const nextAvailableAt = typeof record.nextAvailableAt === 'number' ? record.nextAvailableAt : null;
  const soldOutUntil = typeof record.soldOutUntil === 'number' ? record.soldOutUntil : null;
  const autoExpireAt = typeof record.autoExpireAt === 'number' ? record.autoExpireAt : null;
  const categoryAutoExpireAt = typeof record.categoryAutoExpireAt === 'number' ? record.categoryAutoExpireAt : null;
  const currentWindow = normaliseAvailabilityWindow(record.currentWindow);
  const upcomingWindows = Array.isArray(record.upcomingWindows)
    ? (record.upcomingWindows as unknown[]).map(normaliseAvailabilityWindow)
    : [];

  return {
    status,
    isAvailable,
    reasons: normaliseAvailabilityReasons(record.reasons),
    nextAvailableAt,
    currentWindow,
    upcomingWindows,
    soldOutUntil,
    autoExpireAt,
    categoryAutoExpireAt,
  };
};

const DAY_ID_VALUES: DayId[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_ID_SET = new Set<DayId>(DAY_ID_VALUES);

const normaliseDayId = (value: unknown): DayId | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const lower = value.toLowerCase() as DayId;
  return DAY_ID_SET.has(lower) ? lower : null;
};

const normaliseDaypartRule = (input: unknown): DaypartRule | null => {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const source = input as Record<string, unknown>;
  const rawDays = Array.isArray(source.days) ? source.days : [];
  const days = rawDays
    .map(normaliseDayId)
    .filter((day): day is DayId => day !== null);
  const startMinute =
    coerceNullableNumber(
      source.startMinute ?? source.start_minute ?? source.start ?? source.startTime,
    ) ?? 0;
  const endMinute = coerceNullableNumber(
    source.endMinute ?? source.end_minute ?? source.end ?? source.endTime,
  );

  return {
    id: typeof source.id === 'string' ? source.id : undefined,
    label: typeof source.label === 'string' ? source.label : undefined,
    days,
    startMinute,
    endMinute: endMinute ?? startMinute,
  };
};

const normaliseHolidayRule = (input: unknown): HolidayRule | null => {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const source = input as Record<string, unknown>;
  const date = typeof source.date === 'string' ? source.date : '';
  if (!date) {
    return null;
  }
  const startMinute = coerceNullableNumber(
    source.startMinute ?? source.start_minute ?? source.start ?? source.startTime,
  );
  const endMinute = coerceNullableNumber(
    source.endMinute ?? source.end_minute ?? source.end ?? source.endTime,
  );
  return {
    id: typeof source.id === 'string' ? source.id : undefined,
    label: typeof source.label === 'string' ? source.label : undefined,
    date,
    isClosed: typeof source.isClosed === 'boolean' ? source.isClosed : undefined,
    startMinute: startMinute ?? undefined,
    endMinute: endMinute ?? undefined,
  };
};

const normaliseLimitedRunWindow = (input: unknown): LimitedRunWindow | null => {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const source = input as Record<string, unknown>;
  const start = coerceNullableNumber(source.start) ?? coerceNullableNumber(source.startAt) ?? null;
  if (start === null) {
    return null;
  }
  const end =
    coerceNullableNumber(source.end) ?? coerceNullableNumber(source.endAt) ?? null;
  return {
    id: typeof source.id === 'string' ? source.id : undefined,
    label: typeof source.label === 'string' ? source.label : undefined,
    start,
    end,
  };
};

const normaliseAvailabilityRules = (input: unknown): MenuAvailabilityRules => {
  if (!input || typeof input !== 'object') {
    return {
      dayparts: [],
      holidays: [],
      limitedRuns: [],
    };
  }
  const source = input as Record<string, unknown>;
  const dayparts = Array.isArray(source.dayparts)
    ? source.dayparts
        .map((entry) => normaliseDaypartRule(entry))
        .filter((rule): rule is DaypartRule => rule !== null)
    : [];
  const holidays = Array.isArray(source.holidays)
    ? source.holidays
        .map((entry) => normaliseHolidayRule(entry))
        .filter((rule): rule is HolidayRule => rule !== null)
    : [];
  const limitedRuns = Array.isArray(source.limitedRuns)
    ? source.limitedRuns
        .map((entry) => normaliseLimitedRunWindow(entry))
        .filter((rule): rule is LimitedRunWindow => rule !== null)
    : [];

  return {
    timezone: typeof source.timezone === 'string' ? source.timezone : undefined,
    dayparts,
    holidays,
    limitedRuns,
  };
};

const normaliseSpiceLevel = (value: unknown): MenuSpiceLevel | null => (typeof value === 'string' && SPICE_LEVELS.includes(value as MenuSpiceLevel) ? (value as MenuSpiceLevel) : null);

function normaliseMenuItemPayload(payload: unknown): MenuItem {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid menu item payload');
  }
  const source = payload as Record<string, unknown>;
  const idSource = source.id ?? source.item_id ?? source.itemId;
  const id = typeof idSource === 'string' && idSource.trim().length
    ? idSource
    : idSource !== undefined && idSource !== null
    ? String(idSource)
    : '';
  if (!id) {
    throw new Error('Menu item payload missing id');
  }

  const nameSource = source.name;
  const name = typeof nameSource === 'string' && nameSource.trim().length
    ? nameSource
    : 'Unnamed Item';

  const categorySource = source.category_id ?? source.categoryId ?? null;
  const categoryId = typeof categorySource === 'string' && categorySource.trim().length
    ? categorySource
    : categorySource === null
    ? null
    : categorySource !== undefined
    ? String(categorySource)
    : null;

  const descriptionSource = source.description;
  const description = descriptionSource === null || descriptionSource === undefined
    ? null
    : String(descriptionSource);

  const priceSource = source.price_cents ?? source.priceCents;
  const priceCents = coerceNumber(priceSource, 0);

  const legacyAvailabilitySource = source.is_available ?? source.isAvailable;
  const legacyAvailable = typeof legacyAvailabilitySource === 'boolean'
    ? legacyAvailabilitySource
    : legacyAvailabilitySource === undefined
    ? !(source.isEightySixed === true)
    : Boolean(legacyAvailabilitySource);

  const availability = normaliseAvailabilityState(source.availability, legacyAvailable);
  const isAvailable = availability.isAvailable;
  const isEightySixed = availability.status === 'temporarily_unavailable' || (legacyAvailabilitySource === false && !isAvailable);

  const taxRateBps = coerceNullableNumber(source.tax_rate_bps ?? source.taxRateBps) ?? undefined;
  const prepMinutes = coerceNullableNumber(source.prep_minutes ?? source.prepMinutes) ?? undefined;
  const imageSource = source.image_url ?? source.imageUrl;
  const imageUrl = typeof imageSource === 'string' && imageSource.trim().length ? normalizeImageUrl(imageSource) : undefined;
  const sortOrder = coerceNullableNumber(source.sort_order ?? source.sortOrder) ?? 0;

  const tags = Array.isArray(source.tags) ? (source.tags as string[]) : undefined;
  const availabilityRules = normaliseAvailabilityRules(source.availability_rules ?? source.availabilityRules);
  const autoExpireAt = availability.autoExpireAt ?? coerceNullableNumber(source.auto_expire_at ?? source.autoExpireAt);
  const categoryAutoExpireAt = availability.categoryAutoExpireAt ?? coerceNullableNumber(source.category_auto_expire_at ?? source.categoryAutoExpireAt);
  const soldOutUntil = availability.soldOutUntil ?? coerceNullableNumber(source.sold_out_until ?? source.soldOutUntil);
  const imageAltText = typeof source.image_alt_text === 'string' ? source.image_alt_text : typeof source.imageAltText === 'string' ? source.imageAltText : null;
  const imageSpiceLevel = normaliseSpiceLevel(source.image_spice_level ?? source.imageSpiceLevel);

  return {
    id,
    name,
    description,
    priceCents,
    taxRateBps,
    isEightySixed,
    isAvailable,
    categoryId,
    sortOrder,
    availability,
    availabilityRules,
    soldOutUntil,
    autoExpireAt,
    categoryAutoExpireAt,
    modifiers: [],
    modifierGroups: [],
    tags,
    prepMinutes,
    imageUrl,
    imageAltText,
    imageSpiceLevel,
    translations: normaliseItemTranslations(source.translations),
  };
}

function resolveMenuItemFromResponse(payload: unknown): MenuItem {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid menu item response');
  }
  const record = payload as Record<string, unknown>;
  if ('item' in record && record.item) {
    return normaliseMenuItemPayload(record.item);
  }
  if ('menuItem' in record && record.menuItem) {
    return normaliseMenuItemPayload(record.menuItem);
  }
  if (Array.isArray(record.items) && record.items.length > 0) {
    return normaliseMenuItemPayload(record.items[0]);
  }
  return normaliseMenuItemPayload(record);
}

function resolveMenuCategoryFromResponse(payload: unknown): MenuCategory {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid menu category response');
  }
  const record = payload as Record<string, unknown>;
  if ('category' in record && record.category) {
    return normaliseMenuCategory(record.category as MenuApiCategory);
  }
  if (Array.isArray(record.categories) && record.categories.length > 0) {
    return normaliseMenuCategory(record.categories[0] as MenuApiCategory);
  }
  return normaliseMenuCategory(record as MenuApiCategory);
}

export interface FetchMenuOptions {
  signal?: AbortSignal;
  includeInactive?: boolean;
}

export async function fetchMenu(tenantSlug: string, options: FetchMenuOptions = {}): Promise<FetchMenuResponse> {
  const params = new URLSearchParams();
  if (!options.includeInactive) {
    params.set('activeOnly', 'true');
  }

  const url = params.size > 0 ? `/menu?${params.toString()}` : '/menu';
  try {
    const response = await requestJson<MenuApiResponse>(url, {
      signal: options.signal,
      tenantSlug,
      schema: MenuApiResponseSchema,
    });
    const result = normaliseMenuResponse(response);
    const { menu: mergedMenu, conflict } = mergeNetworkMenuSnapshot(tenantSlug, result);
    emitMenuConflictTelemetry(tenantSlug, conflict);
    const cachedAt = Date.now();
    return { ...mergedMenu, cacheStatus: 'network', cachedAt };
  } catch (error) {
    const fallback = recordMenuFallbackUsage(tenantSlug);
    if (fallback) {
      return { ...fallback.payload, cacheStatus: 'cache', cachedAt: fallback.cachedAt };
    }
    throw error;
  }
}

export async function getMenu(tenantSlug: string, seed = false, signal?: AbortSignal): Promise<FetchMenuResponse> {
  const params = new URLSearchParams();
  params.set('includeInactive', 'true');
  if (seed) {
    params.set('seed', 'true');
  }
  const url = params.size > 0 ? `/menu?${params.toString()}` : '/menu';
  try {
    const response = await requestJson<MenuApiResponse>(url, {
      signal,
      tenantSlug,
      schema: MenuApiResponseSchema,
    });
    return normaliseMenuResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error ?? '');
    if (message.includes('404')) {
      return { categories: [] };
    }
    throw error;
  }
}

export interface ToggleItemEightySixResponse {
  item: MenuItem;
}

export async function toggleItemEightySix(
  tenantSlug: string,
  itemId: string,
  options: {
    isEightySixed?: boolean;
    soldOutDurationMinutes?: number;
    soldOutUntil?: number | null;
  },
): Promise<ToggleItemEightySixResponse> {
  const body: Record<string, unknown> = {};
  if (options.isEightySixed !== undefined) {
    body.isEightySixed = options.isEightySixed;
    body.is_available = options.isEightySixed === false;
  }
  if (options.soldOutDurationMinutes !== undefined) {
    body.soldOutDurationMinutes = options.soldOutDurationMinutes;
  }
  if (options.soldOutUntil !== undefined) {
    body.soldOutUntil = options.soldOutUntil;
  }

  try {
    const response = await requestJson<MenuItemEnvelope>(`/menu/items/${encodeURIComponent(itemId)}/availability`, {
      method: 'PATCH',
      body,
      tenantSlug,
      schema: MenuItemEnvelopeSchema,
    });
    return { item: resolveMenuItemFromResponse(response) };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error ?? '');
    if (message && !message.includes('404')) {
      throw error;
    }
    const fallback = await requestJson<MenuItemEnvelope>(`/items/${encodeURIComponent(itemId)}/86`, {
      method: 'PATCH',
      body,
      tenantSlug,
      schema: MenuItemEnvelopeSchema,
    });
    return { item: resolveMenuItemFromResponse(fallback) };
  }
}

export async function updateMenuItemDetails(
  tenantSlug: string,
  itemId: string,
  payload: UpdateMenuItemPayload,
): Promise<MenuItem> {
  const body: Record<string, unknown> = {};
  if (payload.name !== undefined) {
    body.name = payload.name.trim();
  }
  if (payload.description !== undefined) {
    body.description = payload.description === null ? null : payload.description.trim();
  }
  if (payload.priceCents !== undefined) {
    body.priceCents = payload.priceCents;
  }
  if (payload.taxRateBps !== undefined) {
    body.taxRateBps = payload.taxRateBps;
  }
  if (payload.isActive !== undefined) {
    body.isActive = payload.isActive;
  }
  if (payload.categoryId !== undefined) {
    body.categoryId = payload.categoryId;
  }
  if (payload.sortOrder !== undefined) {
    body.sortOrder = payload.sortOrder;
  }
  if (payload.imageUrl !== undefined) {
    if (payload.imageUrl === null) {
      body.imageUrl = null;
    } else {
      const trimmed = payload.imageUrl.trim();
      body.imageUrl = trimmed ? normalizeImageUrl(trimmed) : null;
    }
  }
  if (payload.availabilityRules !== undefined) {
    body.availabilityRules = payload.availabilityRules;
  }
  if (payload.autoExpireAt !== undefined) {
    body.autoExpireAt = payload.autoExpireAt;
  }
  if (payload.imageAltText !== undefined) {
    body.imageAltText = payload.imageAltText;
  }
  if (payload.translations !== undefined) {
    body.translations = prepareTranslationsPayload(payload.translations, ['name', 'description', 'imageAltText']);
  }

  if (payload.imageSpiceLevel !== undefined) {
    body.imageSpiceLevel = payload.imageSpiceLevel;
  }

  const response = await requestJson<MenuItemEnvelope>(`/menu/items/${encodeURIComponent(itemId)}`, {
    method: 'PATCH',
    body,
    tenantSlug,
    schema: MenuItemEnvelopeSchema,
  });
  return resolveMenuItemFromResponse(response);
}

export async function updateMenuCategory(
  tenantSlug: string,
  categoryId: string,
  payload: UpdateMenuCategoryPayload,
): Promise<CategorySummary> {
  const body: Record<string, unknown> = {};
  if (payload.name !== undefined) {
    body.name = payload.name.trim();
  }
  if (payload.sortOrder !== undefined) {
    body.sortOrder = payload.sortOrder;
  }
  if (payload.isActive !== undefined) {
    body.isActive = payload.isActive;
  }
  if (payload.availabilityRules !== undefined) {
    body.availabilityRules = payload.availabilityRules;
  }
  if (payload.autoExpireAt !== undefined) {
    body.autoExpireAt = payload.autoExpireAt;
  }
  if (payload.translations !== undefined) {
    body.translations = prepareTranslationsPayload(payload.translations, ['name', 'description']);
  }

  const response = await requestJson<MenuCategoryEnvelope>(`/menu/categories/${encodeURIComponent(categoryId)}`, {
    method: 'PATCH',
    body,
    tenantSlug,
    schema: MenuCategoryEnvelopeSchema,
  });
  const category = resolveMenuCategoryFromResponse(response);
  return {
    id: category.id,
    name: category.name,
    sortOrder: category.sortOrder,
    createdAt: category.createdAt ?? null,
    isActive: category.isActive ?? true,
    availabilityRules: category.availabilityRules ?? { dayparts: [], holidays: [], limitedRuns: [] },
    autoExpireAt: category.autoExpireAt ?? null,
    translations: category.translations,
  };
}

export async function reorderMenuCategories(
  tenantSlug: string,
  categories: ReorderMenuCategoryInput[],
): Promise<number> {
  const response = await requestJson<{ updated: number }>(`/menu/categories/reorder`, {
    method: 'POST',
    body: { categories },
    tenantSlug,
    schema: UpdatedResponseSchema,
  });
  return response.updated ?? 0;
}

export async function reorderMenuItems(
  tenantSlug: string,
  updates: ReorderMenuItemInput[],
): Promise<number> {
  const response = await requestJson<{ updated: number }>(`/menu/items/reorder`, {
    method: 'POST',
    body: { updates },
    tenantSlug,
    schema: UpdatedResponseSchema,
  });
  return response.updated ?? 0;
}

export interface ReorderModifierGroupInput {
  id: string;
  sortOrder: number;
}

export async function reorderModifierGroups(
  itemId: string,
  updates: ReorderModifierGroupInput[],
  tenantSlug?: string,
): Promise<number> {
  const response = await requestJson<{ updated: number }>(`/menu/items/${encodeURIComponent(itemId)}/modifier-groups/reorder`, {
    method: 'POST',
    body: { updates },
    tenantSlug,
    schema: UpdatedResponseSchema,
  });
  return response.updated ?? 0;
}

export interface ReorderModifierInput {
  id: string;
  sortOrder: number;
}

export async function reorderModifiers(
  groupId: string,
  updates: ReorderModifierInput[],
  tenantSlug?: string,
): Promise<number> {
  const response = await requestJson<{ updated: number }>(
    `/menu/modifier-groups/${encodeURIComponent(groupId)}/modifiers/reorder`,
    {
      method: 'POST',
      body: { updates },
      tenantSlug,
      schema: UpdatedResponseSchema,
    },
  );
  return response.updated ?? 0;
}

export type MenuBulkCategory = {
  id?: string;
  name: string;
  sortOrder?: number;
};

export type MenuBulkItem = {
  id?: string;
  categoryId: string;
  name: string;
  description?: string | null;
  priceCents: number;
  taxRateBps?: number | null;
  sortOrder?: number;
  isActive?: boolean;
  imageUrl?: string | null;
  allergenTags?: string[];
};

export type MenuBulkModifier = {
  id?: string;
  code?: string | null;
  sku?: string | null;
  externalReference?: string | null;
  name: string;
  minQuantity?: number;
  maxQuantity?: number | null;
  priceDeltaCents?: number;
  priceOverrideCents?: number | null;
  isDefault?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  allergenTags?: string[];
};

export type MenuBulkModifierGroup = {
  id?: string;
  itemId: string;
  code?: string | null;
  name: string;
  description?: string | null;
  kind?: 'modifier' | 'variant';
  selectionMode?: 'single' | 'multiple';
  minSelections?: number;
  maxSelections?: number | null;
  stackingBehavior?: 'exclusive' | 'stack' | 'repeatable';
  allowCustomPrice?: boolean;
  allowQuantity?: boolean;
  isRequired?: boolean;
  sortOrder?: number;
  modifiers?: MenuBulkModifier[];
};

export type MenuBulkData = {
  categories: MenuBulkCategory[];
  items: MenuBulkItem[];
  modifierGroups: MenuBulkModifierGroup[];
};

const MenuBulkCategorySchema = z
  .object({
    id: z.string().optional(),
    name: z.string(),
    sortOrder: z.number().optional(),
  })
  .passthrough();

const MenuBulkItemSchema = z
  .object({
    id: z.string().optional(),
    categoryId: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    priceCents: z.number(),
    taxRateBps: z.number().nullable().optional(),
    sortOrder: z.number().optional(),
    isActive: z.boolean().optional(),
    imageUrl: z.string().nullable().optional(),
    allergenTags: z.array(z.string()).optional(),
  })
  .passthrough();

const MenuBulkModifierSchema = z
  .object({
    id: z.string().optional(),
    code: z.string().nullable().optional(),
    sku: z.string().nullable().optional(),
    externalReference: z.string().nullable().optional(),
    name: z.string(),
    minQuantity: z.number().optional(),
    maxQuantity: z.number().nullable().optional(),
    priceDeltaCents: z.number().optional(),
    priceOverrideCents: z.number().nullable().optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().optional(),
    allergenTags: z.array(z.string()).optional(),
  })
  .passthrough();

const MenuBulkModifierGroupSchema = z
  .object({
    id: z.string().optional(),
    itemId: z.string(),
    code: z.string().nullable().optional(),
    name: z.string(),
    description: z.string().nullable().optional(),
    kind: z.enum(['modifier', 'variant']).optional(),
    selectionMode: z.enum(['single', 'multiple']).optional(),
    minSelections: z.number().optional(),
    maxSelections: z.number().nullable().optional(),
    stackingBehavior: z.enum(['exclusive', 'stack', 'repeatable']).optional(),
    allowCustomPrice: z.boolean().optional(),
    allowQuantity: z.boolean().optional(),
    isRequired: z.boolean().optional(),
    sortOrder: z.number().optional(),
    modifiers: z.array(MenuBulkModifierSchema).optional(),
  })
  .passthrough();

const MenuBulkDataSchema = z
  .object({
    categories: z.array(MenuBulkCategorySchema),
    items: z.array(MenuBulkItemSchema),
    modifierGroups: z.array(MenuBulkModifierGroupSchema),
  })
  .passthrough();

const MenuBulkExportResponseSchema = z
  .object({
    ok: z.boolean(),
    data: MenuBulkDataSchema,
  })
  .passthrough();

const MenuBulkImportSummarySchema = z
  .object({
    categoriesInserted: z.number(),
    itemsInserted: z.number(),
    modifierGroupsInserted: z.number(),
    modifiersInserted: z.number(),
  })
  .passthrough();

const MenuBulkImportResponseSchema = z
  .object({
    ok: z.boolean(),
    jobId: z.string(),
    summary: MenuBulkImportSummarySchema,
  })
  .passthrough();

const MenuBulkJobRowSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    jobType: z.enum(['import_csv', 'import_json', 'export_csv', 'export_json', 'rollback']),
    status: z.enum(['pending', 'validating', 'running', 'completed', 'failed', 'cancelled', 'rolled_back']),
    sourceFilename: z.string().nullable(),
    sourceFormat: z.string().nullable(),
    validationSummary: z.record(z.string(), z.unknown()).nullable(),
    progressPercent: z.number(),
    totalRecords: z.number(),
    processedRecords: z.number(),
    errorMessage: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    requestedBy: z.string().nullable(),
    locationId: z.string().nullable(),
    rollbackJobId: z.string().nullable(),
    createdAt: z.number(),
    startedAt: z.number().nullable(),
    completedAt: z.number().nullable(),
    updatedAt: z.number(),
  })
  .passthrough();

const MenuBulkJobsResponseSchema = z
  .object({
    ok: z.boolean(),
    jobs: z.array(MenuBulkJobRowSchema),
  })
  .passthrough();

export type MenuBulkImportEnvelope = {
  format?: 'json' | 'csv';
  sourceFilename?: string | null;
  mappingName?: string | null;
  metadata?: Record<string, unknown>;
  data: MenuBulkData;
};

export interface MenuBulkJobRow {
  id: string;
  tenantId: string;
  jobType: 'import_csv' | 'import_json' | 'export_csv' | 'export_json' | 'rollback';
  status: 'pending' | 'validating' | 'running' | 'completed' | 'failed' | 'cancelled' | 'rolled_back';
  sourceFilename: string | null;
  sourceFormat: string | null;
  validationSummary: Record<string, unknown> | null;
  progressPercent: number;
  totalRecords: number;
  processedRecords: number;
  errorMessage: string | null;
  metadata: Record<string, unknown>;
  requestedBy: string | null;
  locationId: string | null;
  rollbackJobId: string | null;
  createdAt: number;
  startedAt: number | null;
  completedAt: number | null;
  updatedAt: number;
}

export async function exportMenuBulk(tenantSlug?: string): Promise<MenuBulkData> {
  const response = await requestJson<{ ok: boolean; data: MenuBulkData }>('/menu/bulk/export', {
    tenantSlug,
    schema: MenuBulkExportResponseSchema,
  });
  return response.data;
}

const isMenuBulkImportEnvelope = (
  payload: MenuBulkData | MenuBulkImportEnvelope,
): payload is MenuBulkImportEnvelope => {
  return typeof (payload as MenuBulkImportEnvelope).data !== 'undefined';
};

export async function importMenuBulk(
  payload: MenuBulkData | MenuBulkImportEnvelope,
  tenantSlug?: string,
): Promise<{
  jobId: string;
  summary: {
    categoriesInserted: number;
    itemsInserted: number;
    modifierGroupsInserted: number;
    modifiersInserted: number;
  };
}> {
  const requestBody = isMenuBulkImportEnvelope(payload)
    ? {
        ...('format' in payload && payload.format ? { format: payload.format } : { format: 'json' }),
        ...(payload.sourceFilename ? { sourceFilename: payload.sourceFilename } : {}),
        ...(payload.mappingName ? { mappingName: payload.mappingName } : {}),
        ...(payload.metadata ? { metadata: payload.metadata } : {}),
        data: payload.data,
      }
    : payload;

  const response = await requestJson<{ ok: boolean; jobId: string; summary: { categoriesInserted: number; itemsInserted: number; modifierGroupsInserted: number; modifiersInserted: number } }>('/menu/bulk/import', {
    method: 'POST',
    body: requestBody,
    tenantSlug,
    schema: MenuBulkImportResponseSchema,
  });
  return { jobId: response.jobId, summary: response.summary };
}

export async function fetchMenuBulkJobs(
  tenantSlug?: string,
  options: { limit?: number } = {},
): Promise<MenuBulkJobRow[]> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) {
    params.set('limit', String(options.limit));
  }
  const query = params.toString();
  const response = await requestJson<{ ok: boolean; jobs: MenuBulkJobRow[] }>(`/menu/bulk/jobs${query ? `?${query}` : ''}`, {
    tenantSlug,
    schema: MenuBulkJobsResponseSchema,
  });
  return response.jobs;
}

export async function fetchMenuMetrics(
  tenantSlug: string,
  options: { days?: number } = {},
): Promise<FetchMenuMetricsResponse> {
  const params = new URLSearchParams();
  if (options.days !== undefined) {
    params.set('days', String(options.days));
  }
  const query = params.toString();
  return requestJson<FetchMenuMetricsResponse>(`/menu/metrics${query ? `?${query}` : ''}`, {
    tenantSlug,
    schema: MenuMetricsResponseSchema,
  });
}

export async function fetchMenuTopItems(
  tenantSlug: string,
  options: { days?: number } = {},
): Promise<FetchMenuTopItemsResponse> {
  const params = new URLSearchParams();
  if (options.days !== undefined) {
    params.set('days', String(options.days));
  }
  const query = params.toString();
  return requestJson<FetchMenuTopItemsResponse>(`/menu/analytics/top-items${query ? `?${query}` : ''}`, {
    tenantSlug,
    schema: MenuTopItemsResponseSchema,
  });
}

export async function recordMenuEvent(tenantSlug: string, payload: MenuEventPayload): Promise<void> {
  await requestJson<unknown>('/menu/events', {
    method: 'POST',
    body: payload,
    tenantSlug,
  });
}

export interface CategorySummary {
  id: string;
  name: string;
  sortOrder: number;
  createdAt?: string | null;
  translations?: Record<string, { name?: string; description?: string }>;
}

export async function fetchCategories(tenantSlug: string): Promise<CategorySummary[]> {
  const response = await requestJson<MenuApiResponse>('/menu/categories', { tenantSlug, schema: MenuApiResponseSchema });
  return (response.categories ?? []).map((category) => {
    const normalized = normaliseMenuCategory(category as MenuApiCategory);
    return {
      id: normalized.id,
      name: normalized.name,
      sortOrder: normalized.sortOrder,
      createdAt: normalized.createdAt ? String(normalized.createdAt) : null,
      translations: normalized.translations,
    };
  });
}

export interface CreateCategoryInput {
  name: string;
  sortOrder?: number;
  isActive?: boolean;
  availabilityRules?: MenuAvailabilityRules | null;
  autoExpireAt?: number | null;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string; description?: string }>>;
}

export async function createCategory(tenantSlug: string, payload: CreateCategoryInput): Promise<CategorySummary> {
  const translations = prepareTranslationsPayload(payload.translations, ['name', 'description']);
  const body: Record<string, unknown> = {
    name: payload.name.trim(),
  };
  if (typeof payload.sortOrder === 'number') {
    body.sortOrder = payload.sortOrder;
  }
  if (typeof payload.isActive === 'boolean') {
    body.isActive = payload.isActive;
  }
  if (payload.availabilityRules !== undefined) {
    body.availabilityRules = payload.availabilityRules;
  }
  if (payload.autoExpireAt !== undefined) {
    body.autoExpireAt = payload.autoExpireAt;
  }
  if (Object.keys(translations).length > 0) {
    body.translations = translations;
  }
  const response = await requestJson('/menu/categories', {
    method: 'POST',
    body,
    tenantSlug,
    schema: MenuCategoryEnvelopeSchema,
  });
  const category = resolveMenuCategoryFromResponse(response);
  return {
    id: category.id,
    name: category.name,
    sortOrder: category.sortOrder,
    createdAt: null,
    translations: category.translations,
  };
}

export interface FetchItemsOptions {
  signal?: AbortSignal;
  activeOnly?: boolean;
}

export async function fetchItems(options: FetchItemsOptions = {}): Promise<MenuItem[]> {
  const params = new URLSearchParams();
  if (options.activeOnly) {
    params.set('activeOnly', 'true');
  }
  const path = params.size > 0 ? `/items?${params.toString()}` : '/items';
  const response = await requestJson<{ items: MenuItem[] }>(path, { signal: options.signal });
  return response.items;
}

export interface CreateMenuItemPayload {
  name: string;
  priceCents: number;
  categoryId?: string;
  description?: string;
  sortOrder?: number;
  imageUrl?: string;
  isActive?: boolean;
  availabilityRules?: MenuAvailabilityRules;
  autoExpireAt?: number | null;
  imageAltText?: string | null;
  imageSpiceLevel?: MenuSpiceLevel | null;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string; description?: string; imageAltText?: string }>>;
}

export async function createMenuItem(tenantSlug: string, payload: CreateMenuItemPayload): Promise<MenuItem | null> {
  const body: Record<string, unknown> = {
    name: payload.name,
    priceCents: payload.priceCents,
    categoryId:
      payload.categoryId && payload.categoryId.trim().length > 0 ? payload.categoryId : undefined,
  };

  const translations = prepareTranslationsPayload(payload.translations, ['name', 'description', 'imageAltText']);
  if (Object.keys(translations).length > 0) {
    body.translations = translations;
  }

  if (payload.description !== undefined) {
    const trimmed = payload.description.trim();
    if (trimmed.length > 0) {
      body.description = trimmed;
    }
  }

  if (typeof payload.sortOrder === 'number') {
    body.sortOrder = payload.sortOrder;
  }

  if (payload.imageUrl) {
    body.imageUrl = normalizeImageUrl(payload.imageUrl.trim());
  }

  if (typeof payload.isActive === 'boolean') {
    body.isActive = payload.isActive;
  }

  if (payload.availabilityRules) {
    body.availabilityRules = payload.availabilityRules;
  }

  if (payload.autoExpireAt !== undefined) {
    body.autoExpireAt = payload.autoExpireAt;
  }

  if (payload.imageAltText !== undefined) {
    body.imageAltText = payload.imageAltText;
  }

  if (payload.imageSpiceLevel !== undefined && payload.imageSpiceLevel !== null) {
    body.imageSpiceLevel = payload.imageSpiceLevel;
  }

  const response = await requestJson<MenuItemEnvelope>('/menu/items', {
    method: 'POST',
    body,
    tenantSlug,
    schema: MenuItemEnvelopeSchema,
  });

  if (response === undefined || response === null) {
    return null;
  }

  return resolveMenuItemFromResponse(response);
}

export interface FetchModifiersOptions {
  itemId?: string;
  signal?: AbortSignal;
  tenantSlug?: string;
}

export async function fetchModifiers(options: FetchModifiersOptions = {}): Promise<MenuItemModifier[]> {
  const params = new URLSearchParams();
  if (options.itemId) {
    params.set('itemId', options.itemId);
  }
  const path = params.size > 0 ? `/modifiers?${params.toString()}` : '/modifiers';
  const response = await requestJson<{ modifiers: Array<Record<string, unknown>> }>(path, {
    signal: options.signal,
    tenantSlug: options.tenantSlug,
    schema: ModifierListResponseSchema,
  });
  return (response.modifiers ?? []).map(mapModifierResponse);
}

export interface CreateModifierInput {
  groupId: string;
  name: string;
  code?: string | null;
  sku?: string | null;
  externalReference?: string | null;
  minQuantity?: number;
  maxQuantity?: number | null;
  priceDeltaCents?: number;
  priceOverrideCents?: number | null;
  isDefault?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  allergenTags?: string[];
  metadata?: Record<string, unknown>;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string }>>;
  tenantSlug?: string;
}

export async function createModifier(payload: CreateModifierInput): Promise<MenuItemModifier> {
  const translations = prepareTranslationsPayload(payload.translations, ['name']);
  const body: Record<string, unknown> = {
    code: payload.code ?? null,
    name: payload.name,
    sku: payload.sku ?? null,
    externalReference: payload.externalReference ?? null,
    minQuantity: payload.minQuantity ?? 0,
    maxQuantity: payload.maxQuantity ?? null,
    priceDeltaCents: payload.priceDeltaCents ?? 0,
    priceOverrideCents: payload.priceOverrideCents ?? null,
    isDefault: payload.isDefault ?? false,
    isActive: payload.isActive ?? true,
    sortOrder: payload.sortOrder ?? undefined,
    allergenTags: payload.allergenTags ?? [],
    metadata: payload.metadata ?? {},
  };
  if (Object.keys(translations).length > 0) {
    body.translations = translations;
  }
  const response = await requestJson<{ modifier: Record<string, unknown> }>(
    `/menu/modifier-groups/${payload.groupId}/modifiers`,
    {
      method: 'POST',
      body,
      tenantSlug: payload.tenantSlug,
    },
  );
  return mapModifierResponse(response.modifier);
}

export interface UpdateModifierInput {
  modifierId: string;
  name?: string;
  code?: string | null;
  sku?: string | null;
  externalReference?: string | null;
  groupId?: string | null;
  minQuantity?: number;
  maxQuantity?: number | null;
  priceDeltaCents?: number;
  priceOverrideCents?: number | null;
  isDefault?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  allergenTags?: string[];
  metadata?: Record<string, unknown>;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string }>>;
  tenantSlug?: string;
}

export async function updateModifier(payload: UpdateModifierInput): Promise<MenuItemModifier> {
  const { modifierId, translations, ...rest } = payload;
  const body: Record<string, unknown> = {
    ...rest,
    metadata: rest.metadata ?? undefined,
    allergenTags: rest.allergenTags ?? undefined,
  };
  if (translations !== undefined) {
    body.translations = prepareTranslationsPayload(translations, ['name']);
  }
  const response = await requestJson<{ modifier: Record<string, unknown> }>(
    `/menu/modifiers/${modifierId}`,
    {
      method: 'PATCH',
      body,
      tenantSlug: payload.tenantSlug,
    },
  );
  return mapModifierResponse(response.modifier);
}

export async function deleteModifier(modifierId: string, tenantSlug?: string): Promise<void> {
  await requestJson<unknown>(`/menu/modifiers/${modifierId}`, { method: 'DELETE', tenantSlug });
}

export interface FetchModifierGroupsOptions {
  itemId?: string;
  tenantSlug?: string;
  signal?: AbortSignal;
}

export async function fetchModifierGroups(options: FetchModifierGroupsOptions = {}): Promise<MenuModifierGroup[]> {
  const { itemId, tenantSlug, signal } = options;
  const basePath = itemId ? `/menu/items/${itemId}/modifier-groups` : '/menu/modifier-groups';
  const response = await requestJson<ModifierGroupsResponse>(basePath, {
    tenantSlug,
    signal,
    schema: ModifierGroupsResponseSchema,
  });
  return (response.groups ?? []).map(mapGroupResponse);
}

export interface CreateModifierGroupInput {
  itemId: string;
  code?: string | null;
  name: string;
  description?: string | null;
  kind?: 'modifier' | 'variant';
  selectionMode?: 'single' | 'multiple';
  minSelections?: number;
  maxSelections?: number | null;
  stackingBehavior?: 'exclusive' | 'stack' | 'repeatable';
  allowCustomPrice?: boolean;
  allowQuantity?: boolean;
  isRequired?: boolean;
  metadata?: Record<string, unknown>;
  sortOrder?: number;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string; description?: string }>>;
  tenantSlug?: string;
}

export async function createModifierGroup(payload: CreateModifierGroupInput): Promise<MenuModifierGroup> {
  const { itemId, translations, ...rest } = payload;
  const body: Record<string, unknown> = {
    ...rest,
    metadata: rest.metadata ?? {},
  };
  if (translations !== undefined) {
    body.translations = prepareTranslationsPayload(translations, ['name', 'description']);
  }
  const response = await requestJson<ModifierGroupEnvelope>(`/menu/items/${itemId}/modifier-groups`, {
    method: 'POST',
    body,
    tenantSlug: payload.tenantSlug,
    schema: ModifierGroupEnvelopeSchema,
  });
  return mapGroupResponse(response.group);
}

export interface UpdateModifierGroupInput {
  groupId: string;
  code?: string | null;
  name?: string;
  description?: string | null;
  kind?: 'modifier' | 'variant';
  selectionMode?: 'single' | 'multiple';
  minSelections?: number;
  maxSelections?: number | null;
  stackingBehavior?: 'exclusive' | 'stack' | 'repeatable';
  allowCustomPrice?: boolean;
  allowQuantity?: boolean;
  isRequired?: boolean;
  metadata?: Record<string, unknown>;
  sortOrder?: number;
  translations?: Partial<Record<SupportedMenuLanguage, { name?: string; description?: string }>>;
  tenantSlug?: string;
}

export async function updateModifierGroup(payload: UpdateModifierGroupInput): Promise<MenuModifierGroup> {
  const { groupId, translations, ...rest } = payload;
  const body: Record<string, unknown> = {
    ...rest,
    metadata: rest.metadata ?? undefined,
  };
  if (translations !== undefined) {
    body.translations = prepareTranslationsPayload(translations, ['name', 'description']);
  }
  const response = await requestJson<ModifierGroupEnvelope>(`/menu/modifier-groups/${groupId}`, {
    method: 'PATCH',
    body,
    tenantSlug: payload.tenantSlug,
    schema: ModifierGroupEnvelopeSchema,
  });
  return mapGroupResponse(response.group);
}

export async function deleteModifierGroup(groupId: string, tenantSlug?: string): Promise<void> {
  await requestJson<unknown>(`/menu/modifier-groups/${groupId}`, { method: 'DELETE', tenantSlug });
}

function mapModifierResponse(modifier: Record<string, unknown>): MenuItemModifier {
  return {
    id: String(modifier.id ?? ''),
    itemId: typeof modifier.itemId === 'string' ? modifier.itemId : null,
    groupId: typeof modifier.groupId === 'string' ? modifier.groupId : null,
    code: typeof modifier.code === 'string' ? modifier.code : null,
    sku: typeof modifier.sku === 'string' ? modifier.sku : null,
    externalReference:
      typeof modifier.externalReference === 'string' ? modifier.externalReference : null,
    name: String(modifier.name ?? ''),
    minQuantity: Number.isFinite(modifier.minQuantity) ? Number(modifier.minQuantity) : 0,
    maxQuantity:
      modifier.maxQuantity === null || modifier.maxQuantity === undefined
        ? null
        : Number(modifier.maxQuantity),
    priceDeltaCents: Number.isFinite(modifier.priceDeltaCents)
      ? Number(modifier.priceDeltaCents)
      : 0,
    priceOverrideCents:
      modifier.priceOverrideCents === null || modifier.priceOverrideCents === undefined
        ? null
        : Number(modifier.priceOverrideCents),
    isDefault: modifier.isDefault === true,
    isActive: modifier.isActive !== false,
    sortOrder: Number.isFinite(modifier.sortOrder) ? Number(modifier.sortOrder) : 0,
    allergenTags: Array.isArray(modifier.allergenTags)
      ? modifier.allergenTags.filter((tag): tag is string => typeof tag === 'string')
      : [],
    metadata:
      typeof modifier.metadata === 'object' && modifier.metadata !== null
        ? (modifier.metadata as Record<string, unknown>)
        : {},
    createdAt: Number.isFinite(modifier.createdAt) ? Number(modifier.createdAt) : Date.now(),
    updatedAt:
      Number.isFinite(modifier.updatedAt) && modifier.updatedAt !== undefined
        ? Number(modifier.updatedAt)
        : Number.isFinite(modifier.createdAt)
          ? Number(modifier.createdAt)
          : Date.now(),
    deletedAt:
      modifier.deletedAt === null || modifier.deletedAt === undefined
        ? null
        : Number(modifier.deletedAt),
    translations: normaliseModifierTranslations(modifier.translations),
  };
}

function mapGroupResponse(group: Record<string, unknown>): MenuModifierGroup {
  return {
    id: String(group.id ?? ''),
    itemId: String(group.itemId ?? ''),
    code: typeof group.code === 'string' ? group.code : null,
    name: String(group.name ?? ''),
    description: typeof group.description === 'string' ? group.description : null,
    kind: group.kind === 'variant' ? 'variant' : 'modifier',
    selectionMode: group.selectionMode === 'multiple' ? 'multiple' : 'single',
    minSelections: Number.isFinite(group.minSelections) ? Number(group.minSelections) : 0,
    maxSelections:
      group.maxSelections === null || group.maxSelections === undefined
        ? null
        : Number(group.maxSelections),
    stackingBehavior:
      group.stackingBehavior === 'stack'
        ? 'stack'
        : group.stackingBehavior === 'repeatable'
          ? 'repeatable'
          : 'exclusive',
    allowCustomPrice: group.allowCustomPrice === true,
    allowQuantity: group.allowQuantity === true,
    isRequired: group.isRequired === true,
    metadata:
      typeof group.metadata === 'object' && group.metadata !== null
        ? (group.metadata as Record<string, unknown>)
        : {},
    sortOrder: Number.isFinite(group.sortOrder) ? Number(group.sortOrder) : 0,
    createdAt: Number.isFinite(group.createdAt) ? Number(group.createdAt) : Date.now(),
    updatedAt:
      Number.isFinite(group.updatedAt) && group.updatedAt !== undefined
        ? Number(group.updatedAt)
        : Number.isFinite(group.createdAt)
          ? Number(group.createdAt)
          : Date.now(),
    translations: normaliseModifierGroupTranslations(group.translations),
    modifiers: Array.isArray(group.modifiers)
      ? group.modifiers
          .filter(
            (modifier): modifier is Record<string, unknown> =>
              typeof modifier === 'object' && modifier !== null,
          )
          .map(mapModifierResponse)
      : [],
  };
}

export async function deleteMenuItem(tenantSlug: string, itemId: string): Promise<void> {
  await requestJson<unknown>(`/menu/items/${itemId}`, {
    method: 'DELETE',
    tenantSlug,
  });
}
